import {Trans} from '@lingui/macro';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {
  HandlerOf,
  IntersperseItem,
  intersperseNodes,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {isDefined} from '../../../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {DropdownBoxCoordinator} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBoxCoordinator';
import {DropdownItemCheckbox} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {PopoverTransferProps} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {useUnmountCancelToken} from '../../../../../../../srcCommon/helpers/hookHelpers';
import {fuzzySearchBuilder} from '../../../../../../helpers/fuzzySearchHelpers';
import {useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {SafetyNetModel, SafetyNetRequiredTaggingModel} from '../../../../../../models/api/safetyNetModel';
import {ApiTags, TagModel} from '../../../../../../models/api/tagModel';
import {fetchApiAsync} from '../../../../../../services/http/http';
import {getCompany} from '../../../../../../store/data/company/companySelectors';
import {DropdownItemTagIcon} from '../../../../../common/dropdowns/dropdownItemTagIcon';
import {DropdownLoader} from '../../../../../common/dropdowns/dropdownLoader';
import {DeprecatedSearchDropdown} from '../../../../../common/dropdowns/search/deprecatedSearchDropdown';
import {SyncDropdownSource} from '../../../../../common/dropdowns/source/syncDropdownSource';
import {TagDropdownItem} from '../../../../../common/dropdowns/tagDropdownItem';

/*
 * Consts.
 */

const MAX_TAGS_TO_RENDER = 3;

/*
 * Props.
 */

interface RequiredTagListProps {
  safetyNet: SafetyNetRequiredTaggingModel;
  selectedTagIds: ReadonlyArray<number>;
  onAddTag: HandlerOf<number>;
  onRemoveTag: HandlerOf<number>;
}

/*
 * Styles.
 */

const StyledSelectedTagContainerDiv = styled.div`
  display: flex;
  align-items: center;
`;

const StyledSelectedTagItemDiv = styled.div`
  display: flex;
  align-items: center;
  margin-right: 5px;
  overflow: hidden;
`;

const StyledSelectedTagItemTextDiv = styled.div`
  ${ellipsis()}
`;

const StyledCommaSeparatorSpan = styled.span`
  color: ${(p) => p.theme.palette.grey.shade40};
`;

const StyledExtraTagsNumberDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const RequiredTagList: React.FC<RequiredTagListProps> = (props) => {
  const {safetyNet, selectedTagIds, onAddTag, onRemoveTag} = props;
  const {isOneTagRequired, name: tagListName} = safetyNet;

  const {tags, onRetry, loadingStatus} = useSafetyNetTags(safetyNet);
  const fuzzySearch = useMemo(() => fuzzySearchBuilder<TagModel>((tag) => tag.name), []);

  const handleSelectTag = useCallback(
    (selectedTagId: number) => {
      if (selectedTagIds.includes(selectedTagId)) {
        return onRemoveTag(selectedTagId);
      }

      return onAddTag(selectedTagId);
    },
    [onAddTag, onRemoveTag, selectedTagIds],
  );

  const selectedTags = useMemo(
    () =>
      selectedTagIds.map((selectedTagId) => tags?.find((tag) => tag.id === selectedTagId)).filter(isDefined),
    [tags, selectedTagIds],
  );

  const dropdownSource = useMemo(
    () =>
      new SyncDropdownSource<TagModel>({
        renderRow: (tag) => {
          const isChecked = selectedTagIds.includes(tag.id);
          return (
            <TagDropdownItem
              tagsPath={[]}
              tag={tag}
              nestingLevel={0}
              onSelect={(selectEvent) => {
                if (!isOneTagRequired) {
                  selectEvent.preventDefault();
                }
                handleSelectTag(tag.id);
              }}
              isChecked={isChecked}
              renderRightContent={
                !isOneTagRequired ? () => <DropdownItemCheckbox isChecked={isChecked} /> : undefined
              }
            />
          );
        },
        rowFilter: (items, query) => (query === '' ? items : fuzzySearch(items, query)), // This allow to keep the inital order.
        items: tags,
      }),
    [tags, handleSelectTag, selectedTagIds, isOneTagRequired, fuzzySearch],
  );

  const renderPopover = (popoverProps: PopoverTransferProps) => {
    if (loadingStatus !== AsyncStatusesEnum.LOADED) {
      // eslint-disable-next-line react/jsx-props-no-spreading
      return <DropdownLoader {...popoverProps} status={loadingStatus} onRetryClick={onRetry} />;
    }

    return (
      <DeprecatedSearchDropdown
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...popoverProps}
        title={tagListName}
        source={dropdownSource}
        width={380}
      />
    );
  };

  return (
    <DropdownBoxCoordinator
      label={tagListName}
      renderDropdown={renderPopover}
      value={renderSelectedTags(selectedTags)}
      isRequired
    />
  );
};

/*
 * Helpers.
 */

function renderSelectedTags(tags: ReadonlyArray<TagModel>) {
  if (tags.length === 0) {
    return <Trans id="required-tagging-dialog-default-tags-input">Select tags</Trans>;
  }

  const displayedTags = tags.slice(0, MAX_TAGS_TO_RENDER);
  const extraTagsNumber = tags.slice(MAX_TAGS_TO_RENDER).length;

  const nodes = displayedTags.map(
    (tag): IntersperseItem => ({
      node: (
        <StyledSelectedTagItemDiv>
          <DropdownItemTagIcon tag={tag} nestingLevel={0} />
          <StyledSelectedTagItemTextDiv>{tag.name}</StyledSelectedTagItemTextDiv>
        </StyledSelectedTagItemDiv>
      ),
      key: String(tag.id),
    }),
  );

  return (
    <StyledSelectedTagContainerDiv>
      {intersperseNodes(nodes, () => (
        <StyledCommaSeparatorSpan>,</StyledCommaSeparatorSpan>
      ))}
      {extraTagsNumber ? <StyledExtraTagsNumberDiv>+{extraTagsNumber}</StyledExtraTagsNumberDiv> : null}
    </StyledSelectedTagContainerDiv>
  );
}

function useSafetyNetTags(safetyNet: SafetyNetModel) {
  const company = useAppSelector(getCompany);

  const [tags, setTags] = useState<ReadonlyArray<TagModel>>([]);
  const [loadingStatus, setLoadingStatus] = useState<AsyncStatusesEnum>(AsyncStatusesEnum.LOADING);

  const cancelToken = useUnmountCancelToken();

  const loadTags = useCallback(() => {
    setLoadingStatus(AsyncStatusesEnum.LOADING);
    fetchApiAsync(ApiTags, `${company.url}/safety_nets/${safetyNet.id}/dependencies`, {cancelToken})
      .then((fetchedTags) => {
        setTags(fetchedTags);
        setLoadingStatus(AsyncStatusesEnum.LOADED);
      })
      .catch(() => setLoadingStatus(AsyncStatusesEnum.FAILED));
  }, [cancelToken, company.url, safetyNet.id]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  return {tags, loadingStatus, onRetry: loadTags};
}
